import { TimePicker } from '@components/TimePicker';
import { Box, DatePicker, Stack, TextField } from '@atgfr8/atg-ui-tailwind';
import { Button, ToastType } from '@components';
import { createForm } from '@felte/solid';
import { validator } from '@felte/validator-yup';
import {
  AppointmentReminderModel,
  setAppointmentReminder,
} from '@store/orders';
import type { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { handleToast } from '@utils/utils';
import { DateTime } from 'luxon';
import * as yup from 'yup';

import cls from './classes';

interface SetAppReminderProps {
  onSubmit: () => void;
  type: 'Load' | 'Prospect';
  showTitle?: boolean;
  class?: string;
  changeTrackingSource?: ChangeTrackingSource;
}
export const SetAppReminder = (props: SetAppReminderProps) => {
  const { form, errors, data, setFields } = createForm({
    initialValues: {
      title: '',
      description: '',
      time: DateTime.now()
        .setZone('local')
        .set({
          hour: 12,
          minute: 30,
          second: 0,
          millisecond: 0,
        })
        .toISO({
          includeOffset: false,
          suppressMilliseconds: true,
        }),
      date: DateTime.now().setZone('local').toISO(),
      startTime: DateTime.now().setZone('local').toISO(),
      endTime: DateTime.now().plus({ minutes: 30 }).setZone('local').toISO(),
      objectIdType: props.type,
      public: false,
    },
    extend: validator({
      schema: yup.object().shape({
        title: yup.string().required('Title is required'),
        description: yup
          .string()
          .required(
            `${
              props.type === 'Prospect' ? 'Details' : 'Description'
            } is required`,
          ),
        time: yup.string().required(),
        date: yup.string().required(),
      }),
    }),
    onSubmit: async (values) => {
      const date = values.date?.split('T')[0];
      const time = values.time?.split('T')[1];
      const startTime = DateTime.fromISO(`${date}T${time}`, {
        zone: 'utc',
      })
        .setZone('local')
        .toUTC()
        .toISO();
      const endTime = DateTime.fromISO(`${date}T${time}`, {
        zone: 'utc',
      })
        .plus({ minutes: 30 })
        .setZone('local')
        .toUTC()
        .toISO();

      const payload = {
        startTime: startTime,
        endTime: endTime,
        title: values.title,
        description: values.description,
        date: DateTime.fromISO(String(values.date)).toFormat('L/d/yyyy'),
        time: DateTime.fromISO(String(values.time)).toFormat('hh:mm a'),
        objectIdType: values.objectIdType,
        public: false,
      };

      const res = await setAppointmentReminder(
        payload as AppointmentReminderModel,
        props.changeTrackingSource,
      );
      if (Boolean(res)) {
        handleToast(
          ToastType.Success,
          props.type === 'Prospect'
            ? 'Reminder created successfully'
            : 'Setting appointment reminder Successfully',
        );
        props.onSubmit();
      }
    },
  });

  const handleTimeChange = (newTime: string) => {
    setFields('time', newTime.includes('T:00') ? '' : newTime);
  };

  const title = {
    Load: 'Set App Reminder',
    Prospect: 'Prospect Followup',
  };
  return (
    <form ref={form} class={props.class ?? cls.popoverClass} id="Set">
      {props.showTitle !== false && (
        <span class={`${cls.modalHeadingLeftAlign}`}>{title[props.type]}</span>
      )}
      <Stack spacing={2} class="p-4">
        <TextField
          label="Title"
          placeholder="Title"
          maxLength={100}
          value={data().title}
          onChange={(itm: string) => setFields('title', itm)}
          error={errors().title}
          dataTestId="title-textInput"
        />
        <DatePicker
          value={data().date!}
          handleChange={(date) => setFields('date', date)}
          error={errors().date as string[]}
          dataTestId="date-datePicker"
        />
        <TimePicker
          label=""
          value={data().time!}
          onChange={handleTimeChange}
          error={errors().time}
          dataTestId="time-timePicker"
        />
        <TextField
          name="description"
          maxLength={1000}
          label={props.type === 'Prospect' ? 'Details' : 'Description'}
          multiline
          placeholder="Body"
          rows={3}
          value={data().description}
          onChange={(itm: string) => setFields('description', itm)}
          error={errors().description}
          dataTestId={'details-description-textInput'}
        />
        {/* </div> */}
        <Box display="flex" alignItems="center" justifyContent="end" gap={2}>
          <Button
            type="submit"
            label="Set Reminder"
            dataTestId={formatDataTestId('Set Reminder', 'button')}
          />
          <Button
            variant="text"
            label="Cancel"
            onClick={() => {
              props.onSubmit();
            }}
            dataTestId={formatDataTestId('Cancel', 'button')}
          />
        </Box>
      </Stack>
    </form>
  );
};
